import React, { useState } from 'react';
import SchemaModal from '../ui/SchemaModal';
import Form from '../ui/Form';
import Input from '../ui/Input';
import ButtonSpinner from '../ui/ButtonSpinner';
import { useToast } from '../ui/Toast';
import { useAuth } from '../context/AuthContext';
import { apiClient } from '../api/apiClient';
import { errorMessage } from '../api/errorMessage';
import { PASSWORD_HINT } from './AuthPage';

const LEER = { current: '', next: '', confirm: '' };

/**
 * Ordnet eine Server-Meldung dem Feld zu, um das es geht — oder gibt null zurück, wenn die
 * Meldung zu keinem Feld gehört (Rate-Limit, Server- und Netzwerkfehler). Nur die Meldungen
 * aus `changePassword` (`backend/controllers/authController.ts`) haben ein Feld; alles andere
 * am Formular anzuzeigen würde den Nutzer an die falsche Stelle schicken.
 */
function feldZurMeldung(meldung) {
  if (meldung === 'Das aktuelle Passwort ist falsch.') return 'current';
  if (meldung === 'Passwörter stimmen nicht überein.') return 'confirm';
  if (meldung === 'Das neue Passwort muss sich vom aktuellen unterscheiden.') return 'next';
  // Die Regelmeldungen aus `validatePasswordRules` beginnen mit „Passwort muss" (Untergrenze)
  // oder „Passwort darf" (Obergrenze). Breiter matchen geht nicht: die Fallback-Meldung
  // „Passwort ändern fehlgeschlagen …" beginnt auch mit „Passwort " und gehört in den Toast.
  if (meldung.startsWith('Passwort muss ') || meldung.startsWith('Passwort darf ')) return 'next';
  return null;
}

export default function ChangePasswordModal({ open, onOpenChange }) {
  const [werte, setWerte] = useState(LEER);
  const [fehler, setFehler] = useState({});
  const [wirdGesendet, setWirdGesendet] = useState(false);
  const { push } = useToast();
  const { currentUser, login } = useAuth();

  const setzeWert = (feld) => (e) => {
    setWerte((v) => ({ ...v, [feld]: e.target.value }));
    setFehler((f) => ({ ...f, [feld]: undefined }));
  };

  const handleClose = (offen) => {
    if (!offen) {
      setWerte(LEER);
      setFehler({});
    }
    onOpenChange(offen);
  };

  const absenden = async (e) => {
    e.preventDefault();
    if (wirdGesendet) return;

    if (!werte.current || !werte.next || !werte.confirm) {
      setFehler({
        current: !werte.current ? 'Bitte ausfüllen.' : undefined,
        next: !werte.next ? 'Bitte ausfüllen.' : undefined,
        confirm: !werte.confirm ? 'Bitte ausfüllen.' : undefined,
      });
      return;
    }
    if (werte.next !== werte.confirm) {
      setFehler({ confirm: 'Die Passwörter stimmen nicht überein.' });
      return;
    }

    setWirdGesendet(true);
    try {
      const { data } = await apiClient.post('/api/auth/change-password', {
        currentPassword: werte.current,
        newPassword: werte.next,
        newPasswordConfirm: werte.confirm,
      });
      // Das eigene Gerät bleibt angemeldet, weil der Server ein frisches Token mitliefert;
      // alle älteren Tokens sind ab jetzt ungültig.
      login(currentUser, data.token);
      push({ tone: 'success', message: 'Passwort geändert. Andere Geräte wurden abgemeldet.' });
      handleClose(false);
    } catch (err) {
      const meldung = errorMessage(err, 'Passwort ändern fehlgeschlagen. Bitte später erneut versuchen.');
      const feld = feldZurMeldung(meldung);
      if (feld) {
        setFehler({ [feld]: meldung });
      } else {
        push({ tone: 'error', message: meldung });
      }
    } finally {
      setWirdGesendet(false);
    }
  };

  return (
    <SchemaModal
      open={open}
      onOpenChange={handleClose}
      size="sm"
      title="Passwort ändern"
      description="Andere angemeldete Geräte werden dabei abgemeldet."
      dismissOnBackdrop={false}
    >
      <Form onSubmit={absenden}>
        <Form.Field label="Aktuelles Passwort" required error={fehler.current}>
          <Input
            type="password"
            aria-label="Aktuelles Passwort"
            autoComplete="current-password"
            value={werte.current}
            onChange={setzeWert('current')}
          />
        </Form.Field>
        <Form.Field
          label="Neues Passwort"
          required
          hint={PASSWORD_HINT}
          error={fehler.next}
        >
          <Input
            type="password"
            aria-label="Neues Passwort"
            autoComplete="new-password"
            value={werte.next}
            onChange={setzeWert('next')}
          />
        </Form.Field>
        <Form.Field label="Neues Passwort wiederholen" required error={fehler.confirm}>
          <Input
            type="password"
            aria-label="Neues Passwort wiederholen"
            autoComplete="new-password"
            value={werte.confirm}
            onChange={setzeWert('confirm')}
          />
        </Form.Field>
        <Form.Actions>
          <button type="button" className="btn-secondary" onClick={() => handleClose(false)}>
            Abbrechen
          </button>
          <button type="submit" disabled={wirdGesendet}>
            {wirdGesendet && <ButtonSpinner size={14} />} {wirdGesendet ? 'Wird geändert…' : 'Passwort ändern'}
          </button>
        </Form.Actions>
      </Form>
    </SchemaModal>
  );
}
